import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';
import { ui } from '@procuros/datachecks';

import { isDocumentTypeWithLocations, isDocumentTypeWithTransportUnits } from 'services/documentTypes/documentTypes';
import { DocumentType, PriceCatalogRoutes, TransactionsRoutes } from 'support/types';

import { DisplayModes } from 'components/DataTable/Types';
import { useRemovePercentageColumn } from 'components/WebEDI/hooks/useRemovePercentageColumn';

import { ModificationGroupsModalTrigger } from '../components/ModificationGroupsModalTrigger';

const PARTIAL_QUANTITY_FIELD_PATH = 'quantity';
const useStatusAndDeleteConfig = (
  config: ui.LineItemsSection['fields'],
  documentType: DocumentType,
  isStandaloneCreation: boolean,
) => {
  const quantityField = useMemo(() => {
    if (isStandaloneCreation) {
      return undefined;
    }

    // Credit note quantity field should not be used for status and deletion configuration
    // The user is crediting quantities, so being confirmed item or not does not make sense for this document type.
    // Everything is confirmed for it
    if (documentType === DocumentType.creditNote) {
      return undefined;
    }
    const quantityFields = config.filter(
      ({ path, is_readonly }: any) => path?.includes(PARTIAL_QUANTITY_FIELD_PATH) && !is_readonly,
    );
    return quantityFields[0]?.path;
  }, [config, documentType, isStandaloneCreation]);

  const updatedConfig = useMemo(() => {
    if (isStandaloneCreation) {
      return config;
    }
    return config.map((field, index) => {
      if (index === 0) {
        const extraFields = {
          isStatusColumn: true,
          quantityField,
        };
        return {
          ...field,
          extraFields,
        };
      }
      return field;
    });
  }, [config, isStandaloneCreation, quantityField]);

  if (isStandaloneCreation) {
    return { config, deleteQuantityField: undefined };
  }

  return { config: updatedConfig, deleteQuantityField: quantityField };
};

const useAddModificationGroupsModal = (config: ui.LineItemsSection['fields'], mode: DisplayModes) => {
  return useMemo(() => {
    return mode === DisplayModes.create || mode === DisplayModes.edit
      ? config
      : config.map((field) => {
          if (field.jsonata_expression) {
            return {
              ...field,
              extraFields: {
                rightAddon: <ModificationGroupsModalTrigger />,
              },
            };
          }
          return field;
        });
  }, [config, mode]);
};

const useAddTransportUnits = (config: ui.LineItemsSection['fields'], documentType: DocumentType) => {
  const { t } = useTranslation();
  return useMemo(() => {
    if (!isDocumentTypeWithTransportUnits(documentType)) {
      return config;
    }

    const transportUnitField = {
      path: '_internal.parent_transport_unit',
      type: ui.FieldTypes.select,
      modality: 'MANDATORY' as const,
      label: t('webedi:line_items.field.transport_unit.label'),
      sort_order: -Infinity,
      type_config: {
        options_lookup_field: 'transport_units.unit_identifier',
      },
      extraFields: {
        disabledIfNested: true,
      },
      _internal: {
        level: 0,
        parent_line_item: '',
        parent_transport_unit: '',
      },
    };

    const updatedConfig = [...config];

    //Setting it right after unit of measure
    const unitIndex = updatedConfig.findIndex((field) => field.path === 'item.unit_of_measure');
    if (unitIndex !== -1) {
      updatedConfig.splice(unitIndex + 1, 0, transportUnitField as any);
    }

    return updatedConfig;
  }, [config, documentType, t]);
};

const useAddLocations = (config: ui.LineItemsSection['fields'], documentType: DocumentType) => {
  const { t } = useTranslation();
  return useMemo(() => {
    if (!isDocumentTypeWithLocations(documentType)) {
      return config;
    }

    const locationField = {
      path: '_internal.parent_location',
      type: ui.FieldTypes.select,
      modality: 'MANDATORY' as const,
      label: t('webedi:line_items.field.location.label'),
      sort_order: -Infinity,
      type_config: {
        options_lookup_field: 'locations.location.identifiers.gln',
      },
      _internal: {
        level: 0,
        parent_line_item: '',
        parent_location: '',
      },
    };

    const updatedConfig = [...config];

    //Setting it right after unit of measure
    const unitIndex = updatedConfig.findIndex((field) => field.path === 'item.unit_of_measure');
    if (unitIndex !== -1) {
      updatedConfig.splice(unitIndex + 1, 0, locationField as any);
    }

    return updatedConfig;
  }, [config, documentType, t]);
};

const useNameWidth = (config: ui.LineItemsSection['fields']) => {
  return useMemo(() => {
    return config.map((field) => {
      if (field.path === 'item.name') {
        return {
          ...field,
          extraFields: {
            ...(field as { extraFields?: Record<string, any> }).extraFields,
            width: '200px',
          },
        };
      }
      return field;
    });
  }, [config]);
};
export const useDocumentLineItemsConfig = (
  config: ui.LineItemsSection['fields'],
  mode: DisplayModes,
  documentType: DocumentType,
) => {
  const isStandaloneCreation = useIsStandaloneDocumentCreation();
  const { config: updatedConfig, deleteQuantityField } = useStatusAndDeleteConfig(
    config,
    documentType,
    isStandaloneCreation,
  );
  const configWithModificationGroupsModal = useAddModificationGroupsModal(updatedConfig, mode);
  const configWithTransportUnits = useAddTransportUnits(configWithModificationGroupsModal, documentType);
  const configWithLocations = useAddLocations(configWithTransportUnits, documentType);
  const configWithoutModificationsPercentage = useRemovePercentageColumn(configWithLocations);
  const configWithNameWidth = useNameWidth(configWithoutModificationsPercentage);

  return {
    config: configWithNameWidth,
    deleteQuantityField,
  };
};

const useIsStandaloneDocumentCreation = () => {
  const isStandaloneCreation = useMatch(TransactionsRoutes.createStandalone);
  const isProductCatalogCreation = useMatch(PriceCatalogRoutes.create);

  return Boolean(isStandaloneCreation || isProductCatalogCreation);
};
